import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate, useParams, Link } from "react-router-dom";
import { getClientGroupById, getClientGroups, getClientsInGroup, assignToOtherManager } from "../Apis/ClientGroupAPIs";
import { deleteClient, getClientFollowupHistory, bulkUpload, downloadSample } from "../Apis/ClientAPIs";
// import { getClientGroups } from "../Apis/ClientGroupAPIs";
// import { getClients, deleteClient } from "../Apis/ClientAPIs";
import { getImageUrl, server } from "../Apis/api";
import { formatDateUTC, formatDateTime } from "../helper/DateHelper";
import { Tooltip } from "bootstrap/dist/js/bootstrap.bundle.min.js";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import usePermissions from "../hook/userPermission";
import TablePagination from "./TablePagination";

const ClientGroupViewLayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [group, setGroup] = useState(null);
  const [groupClients, setGroupClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedSegment, setSelectedSegment] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [clientsLoading, setClientsLoading] = useState(false);

  // Bulk Upload States
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [showErrorDownloadModal, setShowErrorDownloadModal] = useState(false);
  const [errorDownloadUrl, setErrorDownloadUrl] = useState("");
  const [errorCount, setErrorCount] = useState(0);

  // Reassignment states
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [targetGroupId, setTargetGroupId] = useState("");
  const [managerGroups, setManagerGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  const handleDownloadSample = async () => {
    const groupId = group?._id || group?.id || id || "";
    const managerId =
      typeof group?.manager === "object"
        ? group?.manager?._id || group?.manager?.id || ""
        : group?.manager || "";
    await downloadSample({ groupId, managerId });
  };

  const handleImportClients = async () => {
    if (!selectedFile || !group) return;

    setIsBulkUploading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    const gId = group._id || group.id || id || "";

    formData.append("managerId", "");
    formData.append("groupId", gId);

    const res = await bulkUpload(formData);
    const errUrl = res.response?.data?.errorFileUrl || res.response?.errorFileUrl;
    const errCount = res.response?.data?.errorCount || res.response?.errorCount || 0;

    if (res.status) {
      if (errUrl) {
        setErrorDownloadUrl(errUrl);
        setErrorCount(errCount);
        setShowErrorDownloadModal(true);
      } else {
        toast.success(res.response?.message || "Clients Uploaded Successfully!");
      }
      setShowBulkUploadModal(false);
      setSelectedFile(null);
      fetchGroupDetails(true);
      fetchGroupClients();
    } else {
      if (errUrl) {
        setErrorDownloadUrl(errUrl);
        setErrorCount(errCount);
        setShowErrorDownloadModal(true);
        setShowBulkUploadModal(false);
        setSelectedFile(null);
      }
    }
    setIsBulkUploading(false);
  };

  // Debounce search term to avoid excessive API requests
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Reset page when search term or segment changes
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch, selectedSegment]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedMemberHistory, setSelectedMemberHistory] = useState([]);
  const [selectedMemberName, setSelectedMemberName] = useState("");

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const visibleIds = paginatedClients.map(c => c._id || c.id);
      setSelectedClientIds(prev => Array.from(new Set([...prev, ...visibleIds])));
    } else {
      const visibleIds = paginatedClients.map(c => c._id || c.id);
      setSelectedClientIds(prev => prev.filter(id => !visibleIds.includes(id)));
    }
  };

  const handleSelectClient = (clientId) => {
    setSelectedClientIds((prev) =>
      prev.includes(clientId)
        ? prev.filter((id) => id !== clientId)
        : [...prev, clientId]
    );
  };

  const fetchManagerGroups = async () => {
    setLoadingGroups(true);
    try {
      const res = await getClientGroups({ groupType: 'Manager', limit: 1000 });
      if (res.status) {
        // Exclude current group from target options
        const filtered = (res.response?.data || []).filter(g => String(g._id || g.id) !== String(id));
        setManagerGroups(filtered);
      }
    } catch (err) {
      console.error("Failed to fetch manager groups:", err);
    } finally {
      setLoadingGroups(false);
    }
  };

  useEffect(() => {
    if (showAssignModal) {
      fetchManagerGroups();
    }
  }, [showAssignModal]);

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    if (!targetGroupId) {
      toast.error("Please select a target database group");
      return;
    }
    setIsAssigning(true);
    try {
      const res = await assignToOtherManager({
        clientIds: selectedClientIds,
        targetGroupId: targetGroupId
      });
      if (res.status) {
        toast.success("Clients successfully assigned to target manager");
        setSelectedClientIds([]);
        setShowAssignModal(false);
        setTargetGroupId("");
        fetchGroupClients();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsAssigning(false);
    }
  };

  const handleAddClientClick = () => {
    const managerObj = group.manager || {};
    navigate("/clients/add", {
      state: {
        managerId: typeof group.manager === 'object' ? (group.manager?._id || group.manager?.id || "") : group.manager || "",
        managerName: managerObj.name || "",
        groupId: group._id || group.id || "",
        groupName: group.groupName || "",
        fromGroupView: true
      }
    });
  };

  const fetchGroupDetails = async (skipLoading = false) => {
    if (!id) return;
    if (!skipLoading) setLoading(true);
    try {
      let foundGroup = null;

      // Fetch all groups with a high limit to find our group locally
      try {
        const groupsRes = await getClientGroups({ page: 0, limit: 1000 });
        if (groupsRes.status) {
          const allGroups = groupsRes.response?.data || [];
          foundGroup = allGroups.find((g) => String(g._id || g.id) === String(id));
        }
      } catch (err) {
        console.error("Failed to fetch client groups:", err);
      }

      if (foundGroup) {
        setGroup(foundGroup);
      } else {
        setGroup(null);
      }
    } catch (error) {
      console.error("Failed to fetch client group details:", error);
    } finally {
      if (!skipLoading) setLoading(false);
    }
  };

  const fetchGroupClients = async () => {
    if (!id) return;
    setClientsLoading(true);
    try {
      const clientsRes = await getClientsInGroup(id, {
        page: currentPage,
        limit: rowsPerPage,
        search: debouncedSearch || undefined,
        segment: selectedSegment || undefined
      });
      if (clientsRes.status) {
        setGroupClients(clientsRes.response?.data || []);
        setTotalRecords(clientsRes.response?.total || 0);
      }
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    } finally {
      setClientsLoading(false);
    }
  };

  useEffect(() => {
    fetchGroupDetails();
  }, [id]);

  useEffect(() => {
    fetchGroupClients();
  }, [id, currentPage, rowsPerPage, debouncedSearch, selectedSegment]);

  useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll(
      '[data-bs-toggle="tooltip"]',
    );
    const tooltipList = [...tooltipTriggerList].map((tooltipTriggerEl) => {
      return new Tooltip(tooltipTriggerEl, {
        customClass: "theme-tooltip",
      });
    });
    return () => {
      tooltipList.forEach((tooltip) => tooltip.dispose());
    };
  }, [groupClients]);

  const handleDeleteClick = (member) => {
    setMemberToDelete(member);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (memberToDelete) {
      setIsUpdating(true);
      const res = await deleteClient(memberToDelete._id || memberToDelete.id);
      if (res.status) {
        toast.success("Client Deleted Successfully");
        fetchGroupDetails();
        fetchGroupClients();
        setShowDeleteModal(false);
        setMemberToDelete(null);
      }
      setIsUpdating(false);
    }
  };

  const handleCloseDelete = () => {
    setShowDeleteModal(false);
    setMemberToDelete(null);
  };

  const handleHistoryClick = async (member) => {
    setSelectedMemberName(member.companyName || member.keyPerson || "");
    const managerId = typeof group.manager === 'object' ? (group.manager?._id || group.manager?.id || "") : group.manager || "";
    const groupId = group._id || group.id || "";
    const clientId = member._id || member.id || "";

    try {
      const res = await getClientFollowupHistory(clientId, { managerId, groupId });
      if (res && res.status && res.response) {
        setSelectedMemberHistory(res.response.data || res.response || []);
      } else {
        setSelectedMemberHistory([]);
      }
    } catch (err) {
      console.error("Failed to fetch followup history:", err);
      setSelectedMemberHistory([]);
    }
    setShowHistoryModal(true);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="card p-24 text-center">
        <div className="text-danger fw-bold mb-3">Group Not Found</div>
        <Link to="/client-groups" className="btn btn-primary d-inline-flex align-items-center gap-2 mx-auto">
          <Icon icon="solar:arrow-left-bold" /> Back to Database Groups
        </Link>
      </div>
    );
  }

  const manager = group.manager || {};
  const managerName = manager.name || "N/A";
  const managerEmail = manager.email || "N/A";
  const managerPhone = manager.phoneNumber || "N/A";
  const managerProfileImage = manager.profileImage;

  // Server-side paginated clients
  const paginatedClients = groupClients;

  return (
    <div className="card h-100 p-0 radius-12 overflow-hidden border-0 shadow-sm">
      {/* Header */}
      <div
        className="card-header border-bottom py-16 px-24 d-flex align-items-center justify-content-between flex-wrap gap-3"
        style={{ background: "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)" }}
      >
        <div className="d-flex align-items-center gap-3">
          <h6 className="text-white mb-0 fw-bold">GROUP DETAILS: {group.groupName}</h6>
        </div>
        <div className="d-flex align-items-center gap-2">
          {hasPermission("clients", "add") && (
            <button
              type="button"
              onClick={() => {
                setSelectedFile(null);
                setShowBulkUploadModal(true);
              }}
              className="btn btn-sm px-16 py-8 radius-8 d-flex align-items-center gap-2 shadow-sm fw-bold border-0"
              style={{ backgroundColor: "#f7e7da", color: "black", fontWeight: "600" }}
            >
              <Icon icon="solar:upload-minimalistic-bold" className="text-xl" />
              Bulk Upload
            </button>
          )}
          {hasPermission("clients", "add") && (
            <button
              type="button"
              onClick={handleAddClientClick}
              className="btn btn-sm px-16 py-8 radius-8 d-flex align-items-center gap-2 shadow-sm fw-bold border-0"
              style={{ backgroundColor: "#f7e7da", color: "black", fontWeight: "600" }}
            >
              <Icon icon="ic:baseline-plus" className="text-xl" />
              Add Exhibitor
            </button>
          )}
          <Link
            to="/client-groups"
            className="btn btn-sm px-16 py-8 radius-8 d-flex align-items-center gap-2 shadow-sm fw-bold"
            style={{ backgroundColor: "#f7e7da", color: "black" }}
          >
            <Icon icon="solar:arrow-left-bold" /> Back to Groups
          </Link>
        </div>
      </div>

      <div className="card-body p-24">
        {/* Top Info Section */}
        <div className="row gy-4 mb-24">
          {/* Manager Info Card */}
          <div className="col-12">
            <div className="bg-neutral-50 p-16 radius-12 border shadow-sm position-relative overflow-hidden">
              {group.groupType === "Expo" ? (
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-16">
                  {/* Left Side: Expo Group Info */}
                  <div className="d-flex align-items-center gap-16 flex-wrap">
                    <div className="w-48-px h-48-px rounded-circle bg-danger-100 text-danger-600 d-flex align-items-center justify-content-center fw-bold text-lg shadow-inner">
                      <Icon icon="solar:case-round-bold" className="text-2xl" />
                    </div>
                    <div>
                      <div className="d-flex align-items-center gap-8">
                        <h6 className="mb-0 fw-bold text-dark">Database Type: Expo Group</h6>
                        <span className="badge bg-danger-focus text-danger-main px-8 py-2 radius-pill fw-bold" style={{ fontSize: "10px" }}>
                          EXPO ACCESS
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right Side: Assigned Expo Names */}
                  <div className="d-flex align-items-center gap-8 flex-wrap">
                    <span className="fw-semibold text-secondary-light me-8 text-sm">Assigned to Expos:</span>
                    {group.expos && group.expos.length > 0 ? (
                      group.expos.map((expo) => (
                        <span key={expo._id || expo.id} className="badge bg-white text-dark px-12 py-8 radius-8 border fw-bold text-xs shadow-sm">
                          <Icon icon="solar:point-on-map-bold" className="text-danger me-4 text-sm" />
                          {expo.expoName}
                        </span>
                      ))
                    ) : (
                      <span className="badge bg-white text-danger-main px-12 py-8 radius-8 border fw-bold text-xs shadow-sm">
                        <Icon icon="solar:danger-triangle-bold" className="text-danger me-4 text-sm" />
                        Not Assigned to any Expo
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-16">
                  {/* Left side: Label + Avatar + Name + Leader Badge */}
                  <div className="d-flex align-items-center gap-16 flex-wrap">
                    {managerProfileImage && !imageError ? (
                      <img
                        src={getImageUrl(managerProfileImage)}
                        alt={managerName}
                        className="w-48-px h-48-px rounded-circle border object-fit-cover shadow-sm"
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      <div
                        className="w-48-px h-48-px rounded-circle bg-primary-100 text-primary-600 d-flex align-items-center justify-content-center fw-bold text-lg shadow-inner"
                      >
                        {managerName.charAt(0).toUpperCase()}
                      </div>
                    )}

                    <div>
                      <div className="d-flex align-items-center gap-8">
                        <h6 className="mb-0 fw-bold text-dark">{managerName}</h6>
                        <span className="badge bg-danger-focus text-danger-main px-8 py-2 radius-pill fw-bold" style={{ fontSize: "10px" }}>
                          GROUP LEADER
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Right side: Email & Phone inline */}
                  <div className="d-flex align-items-center gap-16 flex-wrap">
                    <span className="d-flex align-items-center gap-8 fw-medium text-sm text-secondary-light bg-white px-12 py-8 radius-8 border">
                      <Icon icon="solar:letter-bold" className="text-secondary-light text-md" />
                      {managerEmail}
                    </span>
                    <span className="d-flex align-items-center gap-8 fw-medium text-sm text-secondary-light bg-white px-12 py-8 radius-8 border">
                      <Icon icon="solar:phone-bold" className="text-secondary-light text-md" />
                      {managerPhone}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Clients List Section */}
        <div className="border radius-12 bg-white overflow-hidden shadow-sm">
          <div className="p-16 border-bottom bg-neutral-50 d-flex align-items-center justify-content-between flex-wrap gap-3">
            <h6 className="mb-0 fw-bold text-dark d-flex align-items-center gap-2">
              <Icon icon="solar:users-group-two-rounded-bold" className="text-primary-600 text-lg" />
              Assigned Exhibitors ({totalRecords})
            </h6>
            <div className="d-flex align-items-center flex-wrap gap-3">
              {/* Assign to another manager Button */}
              {selectedClientIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowAssignModal(true)}
                  className="btn btn-sm px-16 py-8 radius-8 d-flex align-items-center gap-2 shadow-sm fw-bold border-0 text-white"
                  style={{ backgroundColor: "#bb0007ff", fontWeight: "600" }}
                >
                  <Icon icon="solar:user-plus-bold" className="text-xl" />
                  Assign ({selectedClientIds.length}) to another manager
                </button>
              )}

              {/* Segment Filter */}
              <select
                className="form-select text-xs radius-8"
                style={{ height: "36px", width: "130px" }}
                value={selectedSegment}
                onChange={(e) => setSelectedSegment(e.target.value)}
              >
                <option value="">All Segments</option>
                <option value="B2B">B2B</option>
                <option value="B2C">B2C</option>
                <option value="OTHERS">OTHERS</option>
              </select>

              {/* Search bar */}
              <div className="position-relative w-200-px">
                <input
                  type="text"
                  className="form-control radius-8 ps-32 text-xs"
                  style={{ height: "36px" }}
                  placeholder="Search group clients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Icon
                  icon="ion:search-outline"
                  className="position-absolute top-50 translate-middle-y text-secondary-light"
                  style={{ fontSize: "16px", left: "10px" }}
                />
              </div>
            </div>
          </div>

          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr style={{ backgroundColor: "#f7e7da" }}>
                  <th scope="col" style={{ width: "40px" }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={paginatedClients.length > 0 && paginatedClients.every(c => selectedClientIds.includes(c._id || c.id))}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                    S.No
                  </th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                    Company Name
                  </th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                    Key Person
                  </th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                    Mobile Number
                  </th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                    WhatsApp
                  </th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                    Email
                  </th>
                  {/* <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                    Products
                  </th> */}
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                    Segment
                  </th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                    City
                  </th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                    Ratings
                  </th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                    Status
                  </th>
                  <th scope="col" style={{ color: "black", fontWeight: "bold" }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {clientsLoading ? (
                  <tr>
                    <td colSpan="13" className="text-center py-4">Loading exhibitors...</td>
                  </tr>
                ) : paginatedClients.length > 0 ? (
                  paginatedClients.map((client, index) => (
                    <tr key={client._id || client.id}>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedClientIds.includes(client._id || client.id)}
                          onChange={() => handleSelectClient(client._id || client.id)}
                        />
                      </td>
                      <td>{currentPage * rowsPerPage + index + 1}</td>
                      <td>{client.companyName || '-'}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {client.keyPerson || '-'}
                          </span>
                        </div>
                      </td>
                      <td>{client.mobile1 || '-'}</td>
                      <td className="text-success-600 fw-bold">
                        {client.whatsappNumber || '-'}
                      </td>
                      <td>{client.email || '-'}</td>
                      {/* <td>
                        <div className=" text-secondary-light">
                          {(() => {
                            const productsList = Array.isArray(client.products)
                              ? client.products.map((p) =>
                                typeof p === "object" ? p.productName : p,
                              )
                              : [];

                            if (productsList.length === 0) return "-";
                            if (productsList.length === 1) return productsList[0];

                            return (
                              <div className="d-flex align-items-center gap-1">
                                <span>{productsList[0]}</span>
                                <span
                                  className="badge bg-danger-focus text-danger-main cursor-help"
                                  data-bs-toggle="tooltip"
                                  data-bs-placement="top"
                                  title={productsList.join(", ")}
                                  style={{ fontSize: "10px" }}
                                >
                                  +{productsList.length - 1}
                                </span>
                              </div>
                            );
                          })()}
                        </div>
                      </td> */}
                      <td>
                        <div className="text-secondary-light fw-medium">
                          {client.segment || "-"}
                        </div>
                      </td>
                      <td>
                        <div className="text-secondary-light fw-medium">
                          {client.city || "-"}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-1">
                          <Icon icon="solar:star-bold" className="text-warning" />
                          <span className="fw-bold">
                            {(() => {
                              const r = String(client.ratings || "");
                              const mapping = { "1": "VIP", "2": "ENQUIRY", "3": "OTHER EXHIBITOR", "4": "OWN EXHIBITOR" };
                              return mapping[r] || r || "OTHER EXHIBITOR";
                            })()}
                          </span>
                        </div>
                      </td>
                      <td>
                        <span
                          className={`badge px-12 py-4 radius-pill fw-bold text-sm ${client.isActive ? 'bg-success-focus text-success-600' : 'bg-danger-focus text-danger-600'}`}
                          style={{ minWidth: '80px' }}
                        >
                          {client.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-10">
                          <Link
                            to={`/clients/view/${client._id || client.id}`}
                            className="btn-view-custom fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            style={{
                              backgroundColor: "#eef2ff",
                              color: "#4338ca",
                              border: "1px solid #e0e7ff",
                            }}
                            title="View"
                          >
                            <Icon icon="lucide:eye" className="menu-icon" />
                          </Link>
                          <button
                            type="button"
                            onClick={() => handleHistoryClick(client)}
                            className="fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                            style={{
                              backgroundColor: "#f3e8ff",
                              color: "#7c3aed",
                              border: "1px solid #ddd6fe",
                            }}
                            title="Status History"
                          >
                            <Icon icon="lucide:history" className="text-xl" />
                          </button>
                          {hasPermission("clients", "edit") && (
                            <Link
                              to={`/clients/edit/${client._id || client.id}`}
                              className="btn-edit-custom fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                              title="Edit"
                            >
                              <Icon icon="lucide:edit" className="menu-icon" />
                            </Link>
                          )}
                          {hasPermission("clients", "delete") && (
                            <button
                              type="button"
                              onClick={() => handleDeleteClick(client)}
                              className="bg-danger-focus text-danger-600 bg-hover-danger-200 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                              title="Delete"
                            >
                              <Icon icon="lucide:trash-2" className="text-xl" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="13" className="text-center py-4 text-secondary-light">
                      {searchTerm || selectedSegment ? "No matching clients found in this group." : "No clients assigned to this group yet."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalRecords / rowsPerPage) || 1}
            onPageChange={setCurrentPage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setCurrentPage(0); }}
            totalRecords={totalRecords}
          />
        </div>
      </div>

      <Modal show={showDeleteModal} onHide={handleCloseDelete} centered>
        <Modal.Body className="text-center p-5">
          <div className="d-flex justify-content-center mb-3">
            <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
              <Icon
                icon="lucide:trash-2"
                className="text-danger-600 text-xxl"
              />
            </div>
          </div>
          <h5 className="mb-3">Are you sure?</h5>
          <p className="text-secondary-light mb-4">
            Do you want to delete client "
            {memberToDelete?.companyName || memberToDelete?.fullName}"?
          </p>
          <div className="d-flex justify-content-center gap-3">
            <Button
              variant="outline-secondary"
              className="px-32"
              onClick={handleCloseDelete}
            >
              Cancel
            </Button>
            <Button
              className="px-32 border-0"
              onClick={confirmDelete}
              disabled={isUpdating}
              style={{ backgroundColor: "#ed1c24", color: "white" }}
            >
              {isUpdating ? "Deleting..." : "Confirm"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showHistoryModal} onHide={() => setShowHistoryModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-bottom py-16 px-24 text-white radius-top-16" style={{ background: "linear-gradient(135deg, #000000 0%, #bb0007 50%, #ed1c24 100%)" }}>
          <Modal.Title className="fw-bold text-white mb-0" style={{ fontSize: "16px" }}>
            Status History: {selectedMemberName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-24">
          <div className="table-responsive">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr style={{ background: "linear-gradient(135deg, #000000, #4f1818, #ed1c24)" }}>
                  <th scope="col" style={{ color: "white", fontWeight: "bold", fontSize: "12px", width: "60px" }}>S.No</th>
                  <th scope="col" style={{ color: "white", fontWeight: "bold", fontSize: "12px", width: "130px" }}>Changed By</th>
                  <th scope="col" style={{ color: "white", fontWeight: "bold", fontSize: "12px", width: "150px" }}>Date & Time</th>
                  <th scope="col" style={{ color: "white", fontWeight: "bold", fontSize: "12px", minWidth: "250px" }}>Remarks</th>
                  <th scope="col" style={{ color: "white", fontWeight: "bold", fontSize: "12px", width: "120px" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {selectedMemberHistory && selectedMemberHistory.length > 0 ? (
                  selectedMemberHistory.map((item, index) => (
                    <tr key={index}>
                      <td className="text-xs">{index + 1}</td>
                      <td className="text-xs fw-semibold text-secondary-light">
                        {item.createdByName || "-"}
                      </td>
                      <td className="text-xs text-secondary-light">
                        {formatDateTime(item.date)}
                      </td>
                      <td className="text-xs text-dark fw-medium" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                        {item.remarks || "-"}
                      </td>
                      <td className="text-xs">
                        <span
                          className={`badge px-12 py-4 radius-pill fw-bold ${item.status === 'Converted' ? 'bg-success-focus text-success-main' :
                            item.status === 'High Intent' ? '' :
                              item.status === 'In Discussions' ? 'bg-warning-focus text-warning-main' :
                                item.status === 'Not Interested' ? 'bg-danger-focus text-danger-main' :
                                  item.status === 'Potential' ? 'bg-info-focus text-info-main' :
                                    'bg-neutral-100 text-neutral-600'
                            }`}
                          style={item.status === 'High Intent' ? {
                            backgroundColor: "#f3e8ff",
                            color: "#6d28d9",
                            border: "1px solid #ddd6fe"
                          } : {}}
                        >
                          {item.status || "-"}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-3 text-secondary-light italic text-xs">
                      No status history found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top p-16">
          <Button
            variant="secondary"
            className="px-32 radius-8"
            onClick={() => setShowHistoryModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} centered>
        <Modal.Header closeButton className="border-bottom py-16 px-24 text-white radius-top-16" style={{ background: "linear-gradient(135deg, #000000 0%, #bb0007 50%, #ed1c24 100%)" }}>
          <Modal.Title className="fw-bold text-white mb-0" style={{ fontSize: "16px" }}>
            Assign Clients to Another Manager
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={handleAssignSubmit}>
          <Modal.Body className="p-24">
            <p className="text-secondary-light text-sm mb-4">
              You are assigning <strong>{selectedClientIds.length}</strong> selected clients to another manager. Please select the target database group below:
            </p>
            <div className="mb-3">
              <label htmlFor="targetGroupId" className="form-label fw-bold text-xs text-secondary-light">
                Target Database Group (Manager-based) *
              </label>
              <select
                id="targetGroupId"
                className="form-select radius-8 text-sm"
                value={targetGroupId}
                onChange={(e) => setTargetGroupId(e.target.value)}
                required
                disabled={loadingGroups}
              >
                <option value="">Select Database Group...</option>
                {loadingGroups ? (
                  <option value="" disabled>Loading database groups...</option>
                ) : (
                  managerGroups.map((g) => (
                    <option key={g._id || g.id} value={g._id || g.id}>
                      {g.groupName} - {g.manager?.name || "No Manager"}
                    </option>
                  ))
                )}
              </select>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-top p-16">
            <Button
              variant="outline-secondary"
              className="px-24 radius-8"
              onClick={() => setShowAssignModal(false)}
              disabled={isAssigning}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="px-24 radius-8 border-0"
              style={{ backgroundColor: "#ed1c24", color: "white" }}
              disabled={isAssigning || !targetGroupId}
            >
              {isAssigning ? "Assigning..." : "Assign Clients"}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* Bulk Upload Modal */}
      <Modal
        show={showBulkUploadModal}
        onHide={() => {
          setShowBulkUploadModal(false);
          setSelectedFile(null);
        }}
        centered
        size="lg"
      >
        <div className="modal-content radius-16 border-0 overflow-hidden">
          <div
            className="modal-header py-16 px-24 border-0 d-flex align-items-center justify-content-between"
            style={{
              background:
                "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
            }}
          >
            <h6 className="text-white mb-0">Bulk Upload Clients</h6>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => {
                setShowBulkUploadModal(false);
                setSelectedFile(null);
              }}
            ></button>
          </div>
          <div className="modal-body p-32">
            {group && (
              <div className="mb-16 p-12 radius-8 bg-neutral-100 border text-center">
                <span className="text-xs fw-semibold text-secondary-light uppercase d-block mb-4">Target Group</span>
                <h6 className="mb-0" style={{ color: "#ed1c24" }}>{group.groupName}</h6>
              </div>
            )}

            <div className="text-center mb-24">
              <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px mx-auto mb-16">
                <Icon
                  icon="solar:upload-minimalistic-bold"
                  className="text-danger-600 text-xxl"
                />
              </div>
              <p className="text-secondary-light">
                Download the template, fill in your client data, and upload it
                back to the system.
              </p>
            </div>

            <div className="d-flex flex-column gap-4">
              {/* Download Section */}
              <div className="p-20 radius-12 border bg-neutral-50 d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="text-sm mb-4 fw-bold">
                    Step 1: Download Template
                  </h6>
                  <p className="text-xs text-secondary-light mb-0">
                    Get the standard Excel/CSV format.
                  </p>
                </div>
                <button
                  className="btn btn-outline-danger btn-sm radius-8 d-flex align-items-center gap-2 fw-bold"
                  onClick={handleDownloadSample}
                >
                  <Icon icon="solar:download-bold" />
                  Download
                </button>
              </div>

              {/* Upload Section */}
              <div className="p-20 radius-12 border bg-neutral-50">
                <h6 className="text-sm mb-12 fw-bold">Step 2: Upload File</h6>
                <div
                  className="border-dashed radius-12 p-24 text-center cursor-pointer transition-all"
                  style={{
                    borderColor: "#ed1c24",
                    backgroundColor: "rgba(237, 28, 36, 0.02)",
                    border: "2px dashed #ed1c24",
                  }}
                  onClick={() =>
                    document.getElementById("bulkFileInputGroupView").click()
                  }
                >
                  <input
                    type="file"
                    id="bulkFileInputGroupView"
                    className="d-none"
                    accept=".csv, .xlsx, .xls"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setSelectedFile(e.target.files[0]);
                      }
                      e.target.value = "";
                    }}
                  />
                  {selectedFile ? (
                    <div className="d-flex align-items-center justify-content-center gap-2">
                      <Icon
                        icon="solar:file-check-bold"
                        className="text-success text-2xl"
                      />
                      <span className="text-sm fw-bold text-dark">
                        {selectedFile.name}
                      </span>
                      <Icon
                        icon="line-md:close"
                        className="text-danger cursor-pointer ms-8"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                          const fileInput = document.getElementById("bulkFileInputGroupView");
                          if (fileInput) fileInput.value = "";
                        }}
                      />
                    </div>
                  ) : (
                    <>
                      <Icon
                        icon="solar:cloud-upload-bold"
                        className="text-danger-600 text-4xl mb-8"
                      />
                      <p className="text-sm text-secondary-light mb-0">
                        Click to browse or drag & drop files here
                      </p>
                      <p className="text-xs text-secondary-light opacity-75">
                        Supported formats: CSV, Excel
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer border-top p-16 bg-neutral-50 d-flex justify-content-center gap-3">
            <Button
              variant="outline-secondary"
              className="px-32 radius-8 fw-bold"
              onClick={() => {
                setShowBulkUploadModal(false);
                setSelectedFile(null);
              }}
            >
              Cancel
            </Button>
            <Button
              className="px-32 radius-8 border-0 fw-bold d-flex align-items-center gap-2"
              style={{ backgroundColor: "#ed1c24", color: "white" }}
              disabled={!selectedFile || isBulkUploading}
              onClick={handleImportClients}
            >
              {isBulkUploading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Uploading...
                </>
              ) : (
                <>
                  <Icon icon="solar:check-read-bold" />
                  Upload Clients
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Error Download Modal */}
      <Modal
        show={showErrorDownloadModal}
        onHide={() => {
          setShowErrorDownloadModal(false);
          setErrorDownloadUrl("");
          setErrorCount(0);
        }}
        centered
        size="md"
      >
        <div className="modal-content radius-16 border-0 overflow-hidden">
          <div
            className="modal-header py-16 px-24 border-0 d-flex align-items-center justify-content-between"
            style={{
              background:
                "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
            }}
          >
            <h6 className="text-white mb-0">Bulk Upload Errors</h6>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => {
                setShowErrorDownloadModal(false);
                setErrorDownloadUrl("");
                setErrorCount(0);
              }}
            ></button>
          </div>
          <div className="modal-body p-32 text-center">
            {group && (
              <div className="mb-16 p-12 radius-8 bg-neutral-100 border text-center">
                <span className="text-xs fw-semibold text-secondary-light uppercase d-block mb-4">Database Name</span>
                <h6 className="mb-0 fw-bold" style={{ color: "#ed1c24" }}>{group.groupName}</h6>
              </div>
            )}
            <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px mx-auto mb-24">
              <Icon
                icon="solar:danger-triangle-bold"
                className="text-danger-600 text-xxl"
              />
            </div>
            <h5 className="mb-12" style={{ color: "#ed1c24", fontWeight: "700" }}>
              Some Rows Have Errors!
            </h5>
            <p className="text-secondary-light mb-24">
              We found validation errors in <strong>{errorCount}</strong> row(s) of your uploaded file.
              Please download the error report below to view the specific errors and correct them.
            </p>

            <div className="d-flex justify-content-center gap-3">
              <button
                type="button"
                className="btn btn-outline-secondary px-32 radius-8 fw-bold"
                onClick={() => {
                  setShowErrorDownloadModal(false);
                  setErrorDownloadUrl("");
                  setErrorCount(0);
                }}
              >
                Close
              </button>
              <button
                type="button"
                className="btn px-32 radius-8 fw-bold text-white border-0 d-flex align-items-center gap-2"
                style={{ backgroundColor: "#ed1c24" }}
                onClick={() => {
                  if (errorDownloadUrl) {
                    const link = document.createElement("a");
                    let downloadUrl = errorDownloadUrl;
                    if (!downloadUrl.startsWith("http")) {
                      const cleaned = downloadUrl.startsWith("/") ? downloadUrl : `/${downloadUrl}`;
                      downloadUrl = `${server}${cleaned}`;
                    }
                    link.href = downloadUrl;
                    link.setAttribute("download", `bulk_upload_errors_${Date.now()}.xlsx`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                  }
                }}
              >
                <Icon icon="solar:download-bold" />
                Download Error Report
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <style>{`
        .detail-item {
          border-left: 3px solid #ed1c24;
          padding-left: 12px;
        }
        .shadow-inner {
          box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06);
        }
        .cursor-help {
          cursor: help;
        }
        .theme-tooltip .tooltip-inner {
          background-color: #ed1c24 !important;
          color: white !important;
          font-weight: 600 !important;
          font-size: 12px !important;
          border-radius: 8px !important;
          padding: 8px 12px !important;
          box-shadow: 0 4px 15px rgba(237, 28, 36, 0.25) !important;
        }
        .theme-tooltip .tooltip-arrow::before {
          border-top-color: #ed1c24 !important;
          border-bottom-color: #ed1c24 !important;
        }
      `}</style>
    </div>
  );
};

export default ClientGroupViewLayer;
